import { DOCUMENT } from '@angular/common';
import { Injectable, Injector, OnDestroy, inject } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { AdminSidePanelService } from '@shared/utils/admin-side-panel.service';
import {
  AttachMilincoSheetDragOptions,
  MilincoSheetDragAxis,
  attachMilincoSheetDrag,
} from './milinco-sheet-drag-gesture';

/** Bottom sheets (MatBottomSheet) con asa superior. */
const BOTTOM_SHEET_PANEL_SELECTOR = [
  '.cdk-overlay-pane.milinco-admin-bottom-sheet',
  '.cdk-overlay-pane.milinco-admin-bottom-sheet--wide',
  '.cdk-overlay-pane.product-catalog-bottom-sheet',
  '.cdk-overlay-pane.milinco-mobile-dock-launcher-pane',
  '.cdk-overlay-pane.store-whatsapp-claim-sheet-panel',
  '.cdk-overlay-pane.platform-continue-sheet-panel',
  '.cdk-overlay-pane.sales-board-detail-sheet',
  '.cdk-overlay-pane.pos-drawer-sheet-cdk-pane',
  '.cdk-overlay-pane.service-question-sheet-pane',
].join(', ');

/**
 * Paneles dialog (AdminSidePanel / catálogo).
 * `--from-bottom` → dismiss vertical; resto → swipe hacia la derecha.
 */
const SIDE_PANEL_SELECTOR = [
  '.cdk-overlay-pane.milinco-admin-side-panel',
  '.cdk-overlay-pane.product-catalog-side-panel',
  '.cdk-overlay-pane.product-catalog-menu-card',
].join(', ');

const BOTTOM_CONTAINER_SEL = '.mat-bottom-sheet-container';
const DIALOG_CONTAINER_SEL = '.mat-mdc-dialog-container, .mat-dialog-container';
const ATTACHED_ATTR = 'data-milinco-sheet-drag';

/**
 * Drag-to-dismiss unificado para bottom sheets y side panels Milinco.
 * Se engancha a panelClass conocidos; bootstrap desde `app.config`.
 */
@Injectable({ providedIn: 'root' })
export class MilincoBottomSheetDragDismissService implements OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private observer: MutationObserver | null = null;
  private readonly cleanups = new Map<HTMLElement, () => void>();
  private started = false;

  /** Llamar una vez desde el bootstrap de la app (browser). */
  start(): void {
    if (this.started || typeof MutationObserver === 'undefined') {
      return;
    }
    this.started = true;
    this.scan();
    this.observer = new MutationObserver(() => this.scan());
    this.observer.observe(this.document.body, { childList: true, subtree: true });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.observer = null;
    for (const dispose of this.cleanups.values()) {
      dispose();
    }
    this.cleanups.clear();
  }

  private scan(): void {
    const live = new Set<HTMLElement>();

    this.document.querySelectorAll(BOTTOM_SHEET_PANEL_SELECTOR).forEach((pane) => {
      const container = pane.querySelector(BOTTOM_CONTAINER_SEL) as HTMLElement | null;
      if (!container) {
        return;
      }
      live.add(container);
      this.ensureAttached(container, pane as HTMLElement, 'down', () => this.dismissBottomSheet());
    });

    this.document.querySelectorAll(SIDE_PANEL_SELECTOR).forEach((paneEl) => {
      const pane = paneEl as HTMLElement;
      const container = pane.querySelector(DIALOG_CONTAINER_SEL) as HTMLElement | null;
      if (!container) {
        return;
      }
      live.add(container);
      const fromBottom = pane.classList.contains('milinco-admin-side-panel--from-bottom');
      const axis: MilincoSheetDragAxis = fromBottom ? 'down' : 'end';
      this.ensureAttached(container, pane, axis, () => this.dismissSidePanel(pane));
    });

    for (const [el, dispose] of this.cleanups) {
      if (!live.has(el) || !el.isConnected) {
        dispose();
        this.cleanups.delete(el);
      }
    }
  }

  private ensureAttached(
    container: HTMLElement,
    pane: HTMLElement,
    axis: MilincoSheetDragAxis,
    onDismiss: () => void,
  ): void {
    if (container.getAttribute(ATTACHED_ATTR) === '1') {
      return;
    }
    container.setAttribute(ATTACHED_ATTR, '1');

    const opts: AttachMilincoSheetDragOptions = {
      surface: pane,
      zoneRoot: container,
      axis,
      onDismiss,
    };
    const disposeGesture = attachMilincoSheetDrag(opts);

    this.cleanups.set(container, () => {
      disposeGesture();
      container.removeAttribute(ATTACHED_ATTR);
    });
  }

  private dismissBottomSheet(): void {
    try {
      this.injector.get(MatBottomSheet).dismiss();
    } catch {
      /* sheet already gone */
    }
  }

  private dismissSidePanel(pane: HTMLElement): void {
    try {
      const sidePanels = this.injector.get(AdminSidePanelService);
      if (sidePanels.closeByOverlayPane(pane)) {
        return;
      }
    } catch {
      /* service unavailable */
    }

    try {
      const dialog = this.injector.get(MatDialog);
      const container = pane.querySelector(DIALOG_CONTAINER_SEL) as HTMLElement | null;
      const id = container?.id;
      if (id) {
        const ref = dialog.openDialogs.find((r) => r.id === id);
        if (ref) {
          ref.close();
          return;
        }
      }
    } catch {
      /* ignore */
    }

    const closeBtn = pane.querySelector(
      '.milinco-side-panel-shell__close, button[aria-label]',
    ) as HTMLElement | null;
    closeBtn?.click();
  }
}
